import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { Employee, OffboardingProgress } from '../../types';
import WizardStepper from './WizardStepper';
import DocumentGenerationStep from './steps/DocumentGenerationStep';
import DocumentPackageStep from './steps/DocumentPackageStep';
import SignatureVerificationStep from './steps/SignatureVerificationStep';
import QuickBooksInactivationStep from './steps/QuickBooksInactivationStep';
import { WIZARD_STEPS, type WizardStepKey, type WizardDraftData } from './types';
import { saveDraftToStorage, loadDraftFromStorage, clearDraftFromStorage } from './wizardUtils';
import { offboardingApi, employeeApi, docusignApi } from '../../services/hrApi';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../services/hrApi';
import '../Onboarding/OnboardingWizard.scss';

interface OffboardingWizardProps {
  employeeId: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const OffboardingWizard: React.FC<OffboardingWizardProps> = ({
  employeeId,
  onComplete,
  onCancel,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStepKey>('document_generation');
  const [completedSteps, setCompletedSteps] = useState<WizardStepKey[]>([]);
  const [stepData, setStepData] = useState<Record<WizardStepKey, any>>({} as Record<WizardStepKey, any>);
  const [loading, setLoading] = useState<boolean>(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const lastCompletedStatusRef = useRef<Record<WizardStepKey, boolean>>({} as Record<WizardStepKey, boolean>);
  const backendCompletedStepsRef = useRef<WizardStepKey[]>([]);

  // Load employee data when wizard starts
  useEffect(() => {
    if (employeeId) {
      loadEmployeeData();
    }
  }, [employeeId]);

  // Load employee data and progress
  const loadEmployeeData = async () => {
    setLoading(true);
    try {
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);

      // Load offboarding progress from backend
      let completedStepsFromBackend: WizardStepKey[] = [];
      try {
        const progress = await offboardingApi.getOffboardingProgress(employeeId);
        if (Array.isArray(progress)) {
          completedStepsFromBackend = progress
            .filter((p: OffboardingProgress) => p.completed)
            .map((p: OffboardingProgress) => p.step_name as WizardStepKey);
          setCompletedSteps(completedStepsFromBackend);
          backendCompletedStepsRef.current = completedStepsFromBackend;
          
          // Initialize lastCompletedStatusRef with backend completion status
          completedStepsFromBackend.forEach((step) => {
            lastCompletedStatusRef.current[step] = true;
          });
        }
      } catch (error) {
        console.error('Error loading offboarding progress:', error);
      }

      // Check DocuSign envelope status first to determine navigation
      let envelopeSent = false;
      try {
        const docusignStatus = await docusignApi.checkStatus();
        if (docusignStatus.connected && docusignStatus.valid) {
          const envelopeResult = await docusignApi.getEnvelopeDraft(employeeId, 'employee-offboarding');
          if (envelopeResult.exists) {
            const envelopeStatus = envelopeResult.status || 'created';
            envelopeSent = envelopeStatus === 'sent' || envelopeStatus === 'completed';
            setStepData((prev) => ({
              ...prev,
              document_package: {
                ...prev.document_package,
                package_sent: envelopeSent,
              },
            }));
          }
        }
      } catch (error) {
        console.error('Error checking DocuSign envelope status:', error);
      }

      // Load draft from localStorage and determine initial step
      const draft = loadDraftFromStorage();
      if (draft && draft.employeeId === employeeId) {
        setStepData(draft.stepData || ({} as Record<WizardStepKey, any>));
        // If draft exists, go to document_package step
        setCurrentStep('document_package');
      } else if (envelopeSent) {
        // If envelope is sent, go to signature_verification step
        setCurrentStep('signature_verification');
      } else {
        // No draft exists - determine first incomplete step based on backend progress
        // Find the first step that is not completed
        const firstIncompleteStep = WIZARD_STEPS.find(step => 
          !completedStepsFromBackend.includes(step.key)
        );
        
        if (firstIncompleteStep) {
          setCurrentStep(firstIncompleteStep.key);
        }
        // If all steps are completed, stay on the last step
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };


  // Save draft data to localStorage
  const saveDraftToLocal = useCallback(() => {
    const draftData: WizardDraftData = {
      employeeId,
      stepData,
      completedSteps,
      currentStep,
      updatedAt: new Date().toISOString(),
    };
    saveDraftToStorage(draftData);
  }, [employeeId, stepData, completedSteps, currentStep]);

  // Auto-save form data to localStorage periodically
  const saveDraftToLocalRef = useRef(saveDraftToLocal);
  useEffect(() => {
    saveDraftToLocalRef.current = saveDraftToLocal;
  }, [saveDraftToLocal]);

  useEffect(() => {
    const timer = setTimeout(() => {
      saveDraftToLocalRef.current();
    }, 3000);
    return () => clearTimeout(timer);
  }, [stepData, completedSteps, currentStep]);

  const handleStepChange = async (newStep: WizardStepKey) => {
    // Save current step completion to backend when moving to next step
    if (employeeId && completedSteps.includes(currentStep)) {
      try {
        await offboardingApi.saveOffboardingProgress({
          employee_id: employeeId,
          step_name: currentStep,
          completed: true,
        });
      } catch (error) {
        console.error(`Error saving step completion for ${currentStep}:`, error);
      }
    }

    setCurrentStep(newStep);
    saveDraftToLocal();
  };

  const handleStepDataChange = (step: WizardStepKey, data: any) => {
    setStepData((prev) => ({
      ...prev,
      [step]: data,
    }));
  };

  const handleStepComplete = useCallback((step: WizardStepKey, isComplete: boolean) => {
    // Track previous completion status to detect changes
    const wasComplete = lastCompletedStatusRef.current[step] === true;
    
    // Always update the ref to track completion status
    lastCompletedStatusRef.current[step] = isComplete;
    
    // Don't remove steps that are marked as complete in the backend
    // This prevents step components from accidentally removing completion status
    // when they mount and haven't loaded their data yet
    if (!isComplete && backendCompletedStepsRef.current.includes(step)) {
      // Step is marked complete in backend, don't allow it to be marked incomplete
      // This prevents step components from removing completion status during initial load
      return;
    }
    
    // Save to backend immediately when a step is marked as complete
    // This ensures progress is saved even if user doesn't navigate away from the step
    if (isComplete && !wasComplete && employeeId) {
      // Step just became complete - save to backend immediately
      offboardingApi.saveOffboardingProgress({
        employee_id: employeeId,
        step_name: step,
        completed: true,
      }).catch((error) => {
        console.error(`Error saving step completion for ${step}:`, error);
        // Don't block UI on save error, but log it
      });
    }
    
    // Always update completedSteps array if status changed OR if step is complete but not in array
    // This ensures steps are added even if called multiple times
    setCompletedSteps((prev) => {
      // Protect: don't remove steps that are in backend-completed list
      if (!isComplete && backendCompletedStepsRef.current.includes(step)) {
        return prev;
      }
      
      let updated: WizardStepKey[];
      if (isComplete && !prev.includes(step)) {
        // Step is complete but not in array - add it
        updated = [...prev, step];
      } else if (!isComplete && prev.includes(step)) {
        // Step is incomplete but in array - remove it
        updated = prev.filter((s) => s !== step);
      } else {
        // No change needed
        updated = prev;
      }
      
      // Only save to localStorage if array actually changed
      if (updated !== prev) {
        const draftData: WizardDraftData = {
          employeeId,
          stepData,
          completedSteps: updated,
          currentStep,
          updatedAt: new Date().toISOString(),
        };
        saveDraftToStorage(draftData);
      }
      
      return updated;
    });
  }, [employeeId, stepData, currentStep]);

  // Handle wizard completion
  const handleWizardComplete = async () => {
    if (!employeeId) {
      toast.error('Employee ID is required');
      return;
    }

    try {
      // Save all completed steps to backend before checking completion
      // This ensures steps (especially the last one) are saved even if user didn't click Next
      
      // Include all steps from completedSteps array
      const stepsToSave = new Set(completedSteps);
      
      // CRITICAL: Always check the ref for ALL steps marked as complete
      // This is especially important for the current step (QuickBooks) which might not be in the array yet
      WIZARD_STEPS.forEach((step) => {
        if (lastCompletedStatusRef.current[step.key] === true) {
          stepsToSave.add(step.key);
        }
      });
      
      // Also explicitly ensure current step is saved if it's marked complete in ref
      // This handles the case where QuickBooks step is complete but not in completedSteps array
      if (lastCompletedStatusRef.current[currentStep] === true) {
        stepsToSave.add(currentStep);
      }
      
      console.log('Saving completed steps to backend:', Array.from(stepsToSave));
      console.log('Current completedSteps array:', completedSteps);
      console.log('Current step:', currentStep);
      console.log('Last completed status ref:', lastCompletedStatusRef.current);
      
      // Save all steps
      const savePromises = Array.from(stepsToSave).map((step) => {
        console.log(`Saving step: ${step}`);
        return offboardingApi.saveOffboardingProgress({
          employee_id: employeeId,
          step_name: step,
          completed: true,
        }).then((result) => {
          console.log(`Successfully saved step: ${step}`, result);
          return result;
        }).catch((error) => {
          console.error(`Error saving step completion for ${step}:`, error);
          // Don't throw - continue saving other steps
          return null;
        });
      });
      
      const results = await Promise.all(savePromises);
      console.log('All save results:', results);

      // Check if all steps are completed and update backend
      const result = await offboardingApi.checkOffboardingCompletion(employeeId);
      
      if (result.all_completed) {
        toast.success('Offboarding completed successfully!');
        clearDraftFromStorage();
        if (onComplete) {
          onComplete();
        }
      } else {
        toast.error('Please complete all steps before finishing offboarding');
      }
    } catch (error) {
      console.error('Error completing offboarding:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to complete offboarding: ${errorMessage}`);
    }
  };

  const handleNext = () => {
    const currentIndex = WIZARD_STEPS.findIndex(s => s.key === currentStep);
    if (currentIndex < WIZARD_STEPS.length - 1) {
      handleStepChange(WIZARD_STEPS[currentIndex + 1].key);
    }
  };

  const handlePrevious = () => {
    const currentIndex = WIZARD_STEPS.findIndex(s => s.key === currentStep);
    if (currentIndex > 0) {
      handleStepChange(WIZARD_STEPS[currentIndex - 1].key);
    }
  };

  // Create stable callback for step completion to avoid re-renders
  const stepCompleteCallbacksRef = useRef<Record<WizardStepKey, (isComplete: boolean) => void>>({} as Record<WizardStepKey, (isComplete: boolean) => void>);
  
  useEffect(() => {
    // Create stable callbacks for each step
    WIZARD_STEPS.forEach((step) => {
      stepCompleteCallbacksRef.current[step.key] = (isComplete: boolean) => {
        handleStepComplete(step.key, isComplete);
      };
    });
  }, [handleStepComplete]);

  // After completedSteps is loaded from backend, ensure they stay marked as complete
  // This fixes the issue where step components might call onComplete(false) before their data loads
  useEffect(() => {
    if (backendCompletedStepsRef.current.length > 0 && !loading) {
      // Use a small delay to ensure step components have mounted and state has updated
      const timer = setTimeout(() => {
        // For each backend-completed step, ensure it's in completedSteps
        backendCompletedStepsRef.current.forEach((step) => {
          setCompletedSteps((prev) => {
            if (!prev.includes(step)) {
              // Step is marked complete in backend but not in local state, add it
              return [...prev, step];
            }
            return prev;
          });
          // Also update the ref to mark it as complete
          lastCompletedStatusRef.current[step] = true;
          // Notify the step component that it's complete
          const callback = stepCompleteCallbacksRef.current[step];
          if (callback) {
            callback(true);
          }
        });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [loading]); // Run when loading completes

  const renderStepContent = () => {
    const stepProps = {
      employeeId,
      data: stepData[currentStep],
      onDataChange: (data: any) => handleStepDataChange(currentStep, data),
      onComplete: stepCompleteCallbacksRef.current[currentStep] || ((isComplete: boolean) => handleStepComplete(currentStep, isComplete)),
    };

    switch (currentStep) {
      case 'document_generation':
        return <DocumentGenerationStep {...stepProps} />;
      case 'document_package':
        return <DocumentPackageStep {...stepProps} />;
      case 'signature_verification':
        return <SignatureVerificationStep {...stepProps} />;
      case 'quickbooks_inactivation':
        return <QuickBooksInactivationStep {...stepProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="wizard-container">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '48px', color: '#0dcaf0' }}></i>
          <p style={{ marginTop: '20px', fontSize: '16px', color: '#6c757d' }}>Loading employee data...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="wizard-container">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p style={{ fontSize: '16px', color: '#dc3545' }}>Employee not found</p>
        </div>
      </div>
    );
  }

  const currentIndex = WIZARD_STEPS.findIndex(s => s.key === currentStep);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === WIZARD_STEPS.length - 1;
  const allStepsCompleted = completedSteps.length === WIZARD_STEPS.length;

  return (
    <div className="onboarding-wizard">
      <div className="wizard-container">
        <div className="wizard-header">
          <div>
            <h3>Offboarding Wizard</h3>
            <p className="wizard-description">
              Complete the offboarding process for {employee.first_name} {employee.last_name}
            </p>
          </div>
          {onCancel && (
            <button
              className="btn-cancel-wizard"
              onClick={onCancel}
              style={{
                padding: '8px 16px',
                background: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              <i className="bx bx-x"></i> Cancel
            </button>
          )}
        </div>

        <WizardStepper
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepChange}
        />

        <div className="wizard-content">
          {renderStepContent()}
        </div>

        <div className="wizard-footer">
          <div className="footer-left">
            {!isFirstStep && (
              <button
                className="btn-previous"
                onClick={handlePrevious}
              >
                <i className="bx bx-chevron-left"></i>
                Previous
              </button>
            )}
          </div>
          <div className="footer-right">
            {!isLastStep && (
              <button
                className="btn-next"
                onClick={handleNext}
              >
                Next
                <i className="bx bx-chevron-right"></i>
              </button>
            )}
            {allStepsCompleted && (
              <button className="btn-complete" onClick={handleWizardComplete}>
                <i className="bx bx-check"></i>
                Complete Offboarding
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffboardingWizard;

